import {
  GenericUpdateAction,
  UpdateActionType,
  UpdateActionWithState,
} from "@/models/socketIo/updateAction";
import { otherTeam } from "@/pdf/utils";
import {
  CompletedChallenge,
  Confirmation,
  GameProgress,
  GameState,
  LastChallenge,
  SetStat,
  StartedChallenge,
  Teams,
  UpdateGameStateFunction,
} from "@/models/gameState";
import {
  CurrentLineup,
  LineupPosition,
  Official,
  Player,
  Sanction,
  TeamSquad,
} from "@/models/gameGeneral";
import {
  ExtendedPlayer,
  InitializeMatchPayload,
} from "@/models/socketIo/initializeMatchPayload";
import { Score, TeamReference } from "@/models/pdfData";
import { DeepReadonly } from "@/models/utils";
import { SanctionEventTypeKeys } from "@/models/ticker";
import currentScore from "@components/CurrentScore";
import { isChallengeEqual } from "@/utils/challenge-utils";
import { getScoreFromHistory } from "@/utils/action-history";

export const TESTED_SAMS_SCORE_VERSION = "7.3.1";

export const getUpdateFunction: <K extends UpdateActionType>(
  type: K,
) => UpdateGameStateFunction<K> = (type) => {
  const updateAction = UpdateFunctionMap[type];
  if (!updateAction) {
    console.debug(`Unknown ZUSTAND update`, type);

    return unknownAction;
  } else {
    return updateAction;
  }
};

const unknownAction: UpdateGameStateFunction<any> = (action, state) => {
  return {
    unknownUpdateActionTypes: new Map(state.unknownUpdateActionTypes).set(
      action.type,
      [
        ...(state.unknownUpdateActionTypes.get(action.type) || []),
        action.payload,
      ],
    ),
  };
};

const ignoreAction: UpdateGameStateFunction<any> = (action, state) => {
  return state;
};

const initializeMatch: UpdateGameStateFunction<
  "[MatchState] Initialize Match"
> = (action) => {
  const {
    scoreConfiguration,
    arbitration,
    location,
    matchNumber,
    matchConfiguration,
    team1,
    team2,
    uuid,
    matchSeries: { gender },
    date,
    scoreVersion,
  } = action.payload;
  const teams: GameState["teams"] = {
    team1,
    team2,
  };
  const scoresheetPdfUrl = `https://distributor.sams-score.de/scoresheet/pdf/${uuid}/${matchNumber}.pdf`;
  return {
    samsId: uuid,
    samsScoreVersion: scoreVersion,
    gameProgress: GameProgress.INITIALIZED,
    teams,
    scoreConfiguration,
    arbitration,
    location,
    matchConfiguration,
    matchNumber,
    scoresheetPdfUrl,
    gender,
    startTime: Date.parse(date),
  };
};

const changeJersey: UpdateGameStateFunction<"[Match] Change jersey"> = (
  action,
  state,
) => ({
  teams: {
    ...state.teams,
    [action.payload.teamCode]: {
      ...state.teams[action.payload.teamCode],
      selectedJersey: action.payload.jerseyType,
    },
  },
});

const setShirtColor: UpdateGameStateFunction<"[Match] Set shirt color"> = (
  action,
  state,
) => ({
  teams: {
    ...state.teams,
    [action.payload.teamCode]: {
      ...state.teams[action.payload.teamCode],
      jerseys: {
        ...state.teams[action.payload.teamCode].jerseys,
        custom: {
          ...state.teams[action.payload.teamCode].jerseys.custom,
          shirtColorHex: action.payload.color,
        },
      },
    },
  },
});

const setShortsColor: UpdateGameStateFunction<"[Match] Set shorts color"> = (
  action,
  state,
) => ({
  teams: {
    ...state.teams,
    [action.payload.teamCode]: {
      ...state.teams[action.payload.teamCode],
      jerseys: {
        ...state.teams[action.payload.teamCode].jerseys,
        custom: {
          ...state.teams[action.payload.teamCode].jerseys.custom,
          shortsColorHex: action.payload.color,
        },
      },
    },
  },
});

const togglePlayer: UpdateGameStateFunction<"[TeamSquad] toggle player"> = (
  action,
  state,
) => {
  const { teamMemberId, teamCode } = action.payload;
  const player = getPlayerFromTeam(teamMemberId, state.teams[teamCode]);
  if (player) {
    const teamSquad = getTeamSquadWithToggled(
      "players",
      state.teamSquads[teamCode],
      player,
    );

    return {
      teamSquads: {
        ...state.teamSquads,
        [teamCode]: teamSquad,
      },
    };
  } else {
    console.error(
      `Player with uuid ${teamMemberId} not found in teamSquad for team ${teamCode}`,
    );
    return state;
  }
};

const toggleCaptain: UpdateGameStateFunction<"[TeamSquad] toggle captain"> = (
  action,
  state,
) => {
  const teamSquad = state.teamSquads[action.payload.teamCode];
  const { teamMemberId, teamCode } = action.payload;
  let captain: Player | undefined;
  if (!teamMemberId || teamSquad.captain?.uuid === teamMemberId) {
    captain = undefined;
  } else {
    const newCaptain = getPlayerFromTeam(teamMemberId, state.teams[teamCode]);
    if (!newCaptain) {
      console.error(
        `Toggled game captain with id ${teamMemberId} was not found in ${teamCode}`,
      );
    } else {
      newCaptain.isTeamCaptain = true;
    }
    captain = newCaptain;
  }
  const players = teamSquad.players.map((p) =>
    p.uuid === teamMemberId ? { ...p, isTeamCaptain: true } : p,
  );
  return {
    teamSquads: {
      ...state.teamSquads,
      [teamCode]: { ...teamSquad, captain, players },
    },
  };
};

const toggleLibero: UpdateGameStateFunction<"[TeamSquad] toggle libero"> = (
  action,
  state,
) => {
  const { teamMemberId, teamCode } = action.payload;
  const player = getPlayerFromTeam(teamMemberId, state.teams[teamCode]);
  if (player) {
    player.isLibero = !player.isLibero;
    const teamSquad = getTeamSquadWithToggled(
      "liberos",
      state.teamSquads[teamCode],
      player,
    );
    const players = teamSquad.players.map((p) =>
      p.uuid === teamMemberId ? player : p,
    );

    return {
      teamSquads: {
        ...state.teamSquads,
        [teamCode]: { ...teamSquad, players },
      },
    };
  } else {
    console.error(
      `Libero with uuid ${teamMemberId} not found in teamSquad for team ${teamCode}`,
    );
    return state;
  }
};

const toggleOfficial: UpdateGameStateFunction<"[TeamSquad] toggle official"> = (
  action,
  state,
) => {
  const { teamMemberId, teamCode } = action.payload;
  const official = getOfficialFromTeam(teamMemberId, state.teams[teamCode]);
  if (official) {
    const teamSquad = getTeamSquadWithToggled(
      "officials",
      state.teamSquads[teamCode],
      official,
    );

    return {
      teamSquads: {
        ...state.teamSquads,
        [teamCode]: teamSquad,
      },
    };
  } else {
    console.error(
      `Official with uuid ${teamMemberId} not found in teamSquad for team ${teamCode}`,
    );
    return state;
  }
};

const selectAll: UpdateGameStateFunction<"[TeamSquad] select all"> = (
  action,
  state,
) => {
  const { teamCode, teamMemberIds } = action.payload;
  const nonItcPlayers = state.teams[teamCode].players
    .filter((p) => !p.license.itc)
    .map((p) => p.uuid);
  const team = state.teams[teamCode];

  // There is a bug in SAMS: Already selected ITC players which are not amongst the
  // provided teamMemberIds stay selected when select all is pressed.
  // As we need to mirror this, we need to add them to the list of players to be selected.
  // However, it's easier to just get the list of all players in the teamSquad and add them to the list of players to be selected.
  const buggyPlayersList = state.teamSquads[teamCode].players
    .map((p) => p.uuid)
    .filter(
      (uuid) =>
        teamMemberIds.indexOf(uuid) === -1 &&
        nonItcPlayers.indexOf(uuid) === -1,
    );

  const players = [...nonItcPlayers, ...teamMemberIds, ...buggyPlayersList]
    .map((uuid) => {
      const player = getPlayerFromTeam(uuid, team);
      if (player) {
        return player;
      } else {
        console.error(
          `Player with uuid ${uuid} not found in teamSquad for team ${teamCode}`,
        );
        return undefined;
      }
    })
    .filter((player) => !!player);

  return {
    teamSquads: {
      ...state.teamSquads,
      [teamCode]: { ...state.teamSquads[teamCode], players },
    },
  };
};

const changeJerseyNumber: UpdateGameStateFunction<
  "[Match] Change Jersey Number"
> = (action, state) => {
  const { playerId, jerseyNumber, teamCode } = action.payload;
  const teamSquad = state.teamSquads[teamCode];
  const team = state.teams[teamCode];
  const players = teamSquad.players.map((player) =>
    player.uuid === playerId ? { ...player, jerseyNumber } : player,
  );
  const liberos = teamSquad.liberos.map((player) =>
    player.uuid === playerId ? { ...player, jerseyNumber } : player,
  );
  const teamPlayers = team.players.map((player) =>
    player.uuid === playerId ? { ...player, jerseyNumber } : player,
  );
  return {
    teamSquads: {
      ...state.teamSquads,
      [teamCode]: { ...state.teamSquads[teamCode], players, liberos },
    },
    teams: {
      ...state.teams,
      [teamCode]: { ...state.teams[teamCode], players: teamPlayers },
    },
  };
};

const addOfficial: UpdateGameStateFunction<"[Match] Add Official"> = (
  action,
  state,
) => {
  const { official, teamCode } = action.payload;
  return {
    teams: {
      ...state.teams,
      [teamCode]: {
        ...state.teams[teamCode],
        officials: [...state.teams[teamCode].officials, official],
      },
    },
  };
};

const addPlayer: UpdateGameStateFunction<"[Match] Add Player"> = (
  action,
  state,
) => {
  const { player, teamCode } = action.payload;
  return {
    teams: {
      ...state.teams,
      [teamCode]: {
        ...state.teams[teamCode],
        players: [...state.teams[teamCode].players, player],
      },
    },
  };
};

const editPlayer: UpdateGameStateFunction<"[Match] Edit Player"> = (
  action,
  state,
) => {
  const { player, teamCode } = action.payload;
  return {
    teams: {
      ...state.teams,
      [teamCode]: {
        ...state.teams[teamCode],
        players: [
          ...state.teams[teamCode].players.filter(
            (p) => p.uuid !== player.uuid,
          ),
          player,
        ],
      },
    },
    teamSquads: {
      ...state.teamSquads,
      [teamCode]: {
        ...state.teamSquads[teamCode],
        officials: [
          ...state.teamSquads[teamCode].officials.map((p) => {
            if (p.uuid === player.uuid) {
              return { ...p, ...player };
            } else {
              return p;
            }
          }),
        ],
      },
    },
  };
};

const editOfficial: UpdateGameStateFunction<"[Match] Edit Official"> = (
  action,
  state,
) => {
  const { official, teamCode } = action.payload;
  return {
    teams: {
      ...state.teams,
      [teamCode]: {
        ...state.teams[teamCode],
        officials: [
          ...state.teams[teamCode].officials.filter(
            (p) => p.uuid !== official.uuid,
          ),
          official,
        ],
      },
    },
    teamSquads: {
      ...state.teamSquads,
      [teamCode]: {
        ...state.teamSquads[teamCode],
        officials: [
          ...state.teamSquads[teamCode].officials.map((o) => {
            if (o.uuid === official.uuid) {
              return { ...o, ...official };
            } else {
              return o;
            }
          }),
        ],
      },
    },
  };
};

const loadTeamSquad: UpdateGameStateFunction<"[TeamSquad] load"> = (
  action,
  state,
) => {
  const { teamCode } = action.payload;
  return {
    teamConfirmations: {
      ...state.teamConfirmations,
      [teamCode]:
        state.teamConfirmations[teamCode] === Confirmation.NONE
          ? Confirmation.LOADED
          : Confirmation.SQUAD_RELOADED,
    },
  };
};

const resetTeamSquad: UpdateGameStateFunction<"[TeamSquad] reset all"> = (
  action,
  state,
) => {
  const { teamCode } = action.payload;
  return {
    teamConfirmations: {
      ...state.teamConfirmations,
      [teamCode]: Confirmation.NONE,
    },
    teamSquads: {
      ...state.teamSquads,
      [teamCode]: { players: [], liberos: [], officials: [] },
    },
  };
};

const confirmTeamSquad: UpdateGameStateFunction<"[TeamSquad] confirm"> = (
  action,
  state,
) => {
  const { teamCode } = action.payload;
  return {
    teamConfirmations: {
      ...state.teamConfirmations,
      [teamCode]: Confirmation.SQUAD,
    },
    teamSquads: {
      ...state.teamSquads,
      [teamCode]: { ...state.teamSquads[teamCode], confirmed: true },
    },
  };
};

const undoTeamSquadConfirmation: UpdateGameStateFunction<
  "[TeamSquad] undo confirmation"
> = (action, state) => {
  const { teamCode } = action.payload;
  return {
    teamConfirmations: {
      ...state.teamConfirmations,
      [teamCode]: Confirmation.SQUAD_RELOADED,
    },
  };
};

const approvedPlayerLicense: UpdateGameStateFunction<
  "[Match] Approved player license"
> = (action, state) => {
  return markLicenseWithApprovalState(true, "players", action, state.teams);
};

const deniedPlayerLicense: UpdateGameStateFunction<
  "[Match] Denied player license"
> = (action, state) => {
  return markLicenseWithApprovalState(false, "players", action, state.teams);
};

const approvedOfficialLicense: UpdateGameStateFunction<
  "[Match] Approved official license"
> = (action, state) => {
  return markLicenseWithApprovalState(true, "officials", action, state.teams);
};

const deniedOfficialLicense: UpdateGameStateFunction<
  "[Match] Denied official license"
> = (action, state) => {
  return markLicenseWithApprovalState(false, "officials", action, state.teams);
};

const editStartTime: UpdateGameStateFunction<"[Match] Edit start time"> = (
  action,
) => {
  const newStartTime = action.payload;
  const newStartTimeMillis = Date.parse(newStartTime);
  return { startTime: newStartTimeMillis };
};

const saveComments: UpdateGameStateFunction<"[Match] Save Comments"> = (
  action,
) => ({
  comments: action.payload,
});

const editArbitrationMember: UpdateGameStateFunction<
  "[Match] Edit Arbitration Member"
> = (action, state) => {
  const { type, member } = action.payload;
  return {
    arbitration: { ...state.arbitration, [type]: member },
  };
};

const editNumberOfSpectators: UpdateGameStateFunction<
  "[Match] Edit number of Spectators"
> = (action, state) => ({ ...state, numberOfSpectators: action.payload });

const confirmArbitration: UpdateGameStateFunction<
  "[Match] Confirm arbitration"
> = (action, state) => {
  const isSetterPlayerKnown = [
    ...state.teamSquads.team1.players,
    ...state.teamSquads.team2.players,
  ].some((p) => p.position === "SETTER");

  return {
    gameProgress: GameProgress.ARBITRATION_CONFIRMED,
    isSetterPlayerKnown,
  };
};

const nominate: UpdateGameStateFunction<"[Lineups] Nominate"> = (
  action,
  state,
) => {
  const { teamCode, playerOutIndex, playerInId } = action.payload;
  const player = getPlayerFromTeam(playerInId, state.teamSquads[teamCode]);
  if (player) {
    return {
      currentLineups: {
        ...state.currentLineups,
        [teamCode]: {
          ...state.currentLineups[teamCode],
          [playerOutIndex as LineupPosition]: player,
        },
      },
    };
  } else {
    console.error(
      `Nominated player with ${playerInId} not found in ${teamCode}`,
    );
    return state;
  }
};

const cancelNomination: UpdateGameStateFunction<
  "[Lineups] Cancel nomination"
> = (action, state) => {
  const { teamCode, playerIndex } = action.payload;
  return {
    currentLineups: {
      ...state.currentLineups,
      [teamCode]: {
        ...state.currentLineups[teamCode],
        [playerIndex as LineupPosition]: undefined,
      },
    },
  };
};

const lockStartingSix: UpdateGameStateFunction<
  "[MatchSet] Lock starting six"
> = () => {
  return { gameProgress: GameProgress.STARTING_SIX_LOCKED };
};

const changeServing: UpdateGameStateFunction<"[Serving] Change Serving"> = (
  action,
  state,
) => ({
  serving: otherTeam(state.serving),
  initialServingTeam: otherTeam(state.initialServingTeam),
});

const startMatch: UpdateGameStateFunction<"[TimeKeeping] Start Match"> = (
  action,
  state,
) => ({
  gameProgress: GameProgress.GAME_STARTED,
  currentSet: createNewSet(
    1,
    state.scoreConfiguration.maxTimeoutsPerSet,
    state.scoreConfiguration.maxRegularSubstitutionsPerSet,
    state.scoreConfiguration.numberOfChallengeRequestsByTeam,
    action.payload.actualStartTimeMs,
  ),
});

const changeSetPauseDuration: UpdateGameStateFunction<
  "[Timeouts] Change set pause duration"
> = (action, state) => {
  const { newDuration } = action.payload;
  const lastSetEndTime =
    state.playedSets[state.currentSet.setNumber - 1].endTime;

  const timeDelta = action.receivedTimestamp
    ? action.timestamp - action.receivedTimestamp
    : 0;
  if (lastSetEndTime) {
    return {
      expectedNextSetStartTime: newDuration + (lastSetEndTime - timeDelta),
    };
  } else {
    console.error(
      "Setting the set pause duration even though no set was played yet!",
    );
    return state;
  }
};

const cancelSetPause: UpdateGameStateFunction<
  "[Timeouts] Cancel set pause"
> = () => ({
  isSetPauseOngoing: false,
});

const scoreOverride: UpdateGameStateFunction<"[Set] Score override"> = (
  action,
  state,
) => {
  return updateScore(action.payload, action, state);
};

const score: UpdateGameStateFunction<"[Set] Score"> = (action, state) => {
  const teamCode = action.payload;
  const newScore = state.currentScore[teamCode] + 1;
  let currentScore = { ...state.currentScore, [teamCode]: newScore };

  let { serving, currentLineups } = state;

  if (serving !== teamCode) {
    currentLineups = {
      ...currentLineups,
      [teamCode]: rotateLineupClockwise(
        currentLineups[teamCode],
        state.liberoExchangePlayer?.[teamCode],
      ),
    };
    serving = otherTeam(serving);
  }

  return updateScore(currentScore, action, {
    ...state,
    serving,
    currentLineups,
  });
};

const updateScore = (
  newScore: Score,
  action: GenericUpdateAction<"[Set] Score" | "[Set] Score override">,
  state: DeepReadonly<GameState>,
) => {
  const {
    pointsToWinSet,
    pointsToWinDecidingSet,
    setsToWin,
    minimumPointDifference,
    maximumSets,
    alwaysPlayMaximumNumberOfSets,
  } = state.matchConfiguration;
  let {
    currentSet,
    playedSets,
    currentSetScore,
    serving,
    currentLineups,
    gameProgress,
    isSetPauseOngoing,
    expectedNextSetStartTime,
    teamSides,
    timeFirstScoreInMatch,
    scoreHistory,
  } = state;

  if (!timeFirstScoreInMatch) {
    timeFirstScoreInMatch = action.timestamp;
  }

  scoreHistory = {
    ...scoreHistory,
    [currentSet.setNumber]: [
      ...(scoreHistory[currentSet.setNumber] || []),
      [action.orderRank, newScore.team1, newScore.team2],
    ],
  };

  const pointsToWinThisSet = isDecidingSet(
    currentSet.setNumber,
    setsToWin,
    alwaysPlayMaximumNumberOfSets,
  )
    ? pointsToWinDecidingSet
    : pointsToWinSet;

  const teamWithMorePoints: TeamReference =
    newScore.team1 >= newScore.team2 ? "team1" : "team2";
  const teamWithLessPoints: TeamReference = otherTeam(teamWithMorePoints);

  if (
    newScore[teamWithMorePoints] >= pointsToWinThisSet &&
    state.currentScore[teamWithLessPoints] + minimumPointDifference <=
      newScore[teamWithMorePoints]
  ) {
    // New set
    playedSets = {
      ...playedSets,
      [currentSet.setNumber]: {
        ...currentSet,
        result: newScore,
        endTime: action.timestamp,
      },
    };
    const newSetScore = state.currentSetScore[teamWithMorePoints] + 1;
    currentSetScore = {
      ...state.currentSetScore,
      [teamWithMorePoints]: newSetScore,
    };

    const isLastSet = alwaysPlayMaximumNumberOfSets
      ? Object.values(playedSets).length >= maximumSets
      : currentSetScore[teamWithMorePoints] >= setsToWin;

    const isNotDraw =
      currentSetScore[teamWithMorePoints] >=
      currentSetScore[teamWithLessPoints] + 1;

    const gameFinished = isLastSet && isNotDraw;

    if (gameFinished) {
      gameProgress = GameProgress.GAME_FINISHED;
    } else {
      isSetPauseOngoing = true;
      const timeDelta = action.receivedTimestamp
        ? action.timestamp - action.receivedTimestamp
        : 0;
      expectedNextSetStartTime =
        action.timestamp +
        state.scoreConfiguration.setPauseDurationSeconds * 1000 -
        timeDelta;
      const newSetNumber = currentSet.setNumber + 1;
      currentSet = createNewSet(
        newSetNumber,
        state.scoreConfiguration.maxTimeoutsPerSet,
        state.scoreConfiguration.maxRegularSubstitutionsPerSet,
        state.scoreConfiguration.numberOfChallengeRequestsByTeam,
      );
      newScore = emptyScore();
      currentLineups = { team1: {}, team2: {} };
      serving =
        newSetNumber % 2 === 0
          ? otherTeam(state.initialServingTeam)
          : state.initialServingTeam;
      teamSides = {
        rightTeam: otherTeam(teamSides.rightTeam),
        leftTeam: otherTeam(teamSides.leftTeam),
      };
    }
  }
  return {
    teamSides,
    isSetPauseOngoing,
    expectedNextSetStartTime,
    currentScore: newScore,
    scoreHistory,
    playedSets,
    currentSet,
    currentSetScore,
    serving,
    currentLineups,
    gameProgress,
    timeFirstScoreInMatch,
  };
};

const liberoIn: UpdateGameStateFunction<"[Lineups] Libero in"> = (
  action,
  state,
) => {
  const { teamCode, liberoSubstitute, liberoId } = action.payload;
  const libero = getPlayerFromTeam(liberoId, state.teamSquads[teamCode]);
  const liberoExchangePlayer = getPlayerFromTeam(
    liberoSubstitute,
    state.teamSquads[teamCode],
  );
  if (libero) {
    return {
      liberoExchangePlayer: {
        ...state.liberoExchangePlayer,
        [teamCode]: liberoExchangePlayer,
      },
      currentLineups: {
        ...state.currentLineups,
        [teamCode]: exchangePlayer(
          state.currentLineups[teamCode],
          libero,
          liberoSubstitute,
        ),
      },
    };
  } else {
    console.error(`No libero found with id ${liberoId} in ${teamCode}`);
    return state;
  }
};

const liberoOut: UpdateGameStateFunction<"[Lineups] Libero out"> = (
  action,
  state,
) => {
  const { teamCode, playerInId, liberoId } = action.payload;
  const playerIn = getPlayerFromTeam(playerInId, state.teamSquads[teamCode]);
  if (playerIn) {
    return {
      liberoExchangePlayer: {
        ...state.liberoExchangePlayer,
        [teamCode]: undefined,
      },
      currentLineups: {
        ...state.currentLineups,
        [teamCode]: exchangePlayer(
          state.currentLineups[teamCode],
          playerIn,
          liberoId,
        ),
      },
    };
  } else {
    console.error(`No player found with id ${playerInId} in ${teamCode}`);
    return state;
  }
};

const liberoSwitch: UpdateGameStateFunction<"[Lineups] Libero switch"> = (
  action,
  state,
) => {
  const { liberoInId, liberoOutId, teamCode } = action.payload;
  const liberoIn = getPlayerFromTeam(liberoInId, state.teamSquads[teamCode]);
  if (liberoIn) {
    return {
      currentLineups: {
        ...state.currentLineups,
        [teamCode]: exchangePlayer(
          state.currentLineups[teamCode],
          liberoIn,
          liberoOutId,
        ),
      },
    };
  } else {
    console.error(`No libero found with id ${liberoInId} in ${teamCode}`);
    return state;
  }
};

const liberoUnableToPlay: UpdateGameStateFunction<
  "[Liberos] Libero unable to play"
> = (action, state) => {
  const { teamCode, liberoId } = action.payload;
  const libero = getPlayerFromTeam(liberoId, state.teamSquads[teamCode]);
  if (libero) {
    libero.admissionStatus = "UNABLE_TO_PLAY";

    return {
      teamSquads: {
        ...state.teamSquads,
        [teamCode]: {
          ...state.teamSquads[teamCode],
          liberos: state.teamSquads[teamCode].liberos.map((l) =>
            l.uuid === liberoId ? libero : l,
          ),
          players: state.teamSquads[teamCode].players.map((p) =>
            p.uuid === liberoId ? libero : p,
          ),
        },
      },
    };
  } else {
    return state;
  }
};

const addNewLibero: UpdateGameStateFunction<"[Liberos] Add new libero"> = (
  action,
  state,
) => {
  const { teamCode, playerId } = action.payload;
  const player = getPlayerFromTeam(playerId, state.teams[teamCode]);
  if (player) {
    player.isLibero = true;

    return {
      teamSquads: {
        ...state.teamSquads,
        [teamCode]: {
          ...state.teamSquads[teamCode],
          liberos: [...state.teamSquads[teamCode].liberos, player],
          players: state.teamSquads[teamCode].players.map((p) =>
            p.uuid === playerId ? player : p,
          ),
        },
      },
    };
  } else {
    return state;
  }
};

const startTimeout: UpdateGameStateFunction<"[Timeouts] Start timeout"> = (
  action,
  state,
) => {
  const { teamCode, timestamp, scoreSelf, scoreOther } = action.payload;
  const { currentScore, currentSet } = state;
  const otherTeamCode = otherTeam(teamCode);
  if (
    !action.isUndone &&
    (currentScore[teamCode] !== scoreSelf ||
      currentScore[otherTeamCode] !== scoreOther)
  ) {
    console.error(
      `Calculated score is not matching timeout score! Satz ${currentSet.setNumber}`,
      {
        currentScore,
        timeoutScore: {
          teamCode: scoreSelf,
          otherTeamCode: scoreOther,
        },
        orderRank: action.orderRank,
      },
    );
  }
  const updatedCurrentSet: SetStat = {
    ...currentSet,
    timeoutsRemaining: {
      ...currentSet.timeoutsRemaining,
      [teamCode]: currentSet.timeoutsRemaining[teamCode] - 1,
    },
    timeoutsUsed: {
      ...currentSet.timeoutsUsed,
      [teamCode]: currentSet.timeoutsUsed[teamCode] + 1,
    },
  };
  return {
    currentSet: updatedCurrentSet,
    lastTimeout: {
      teamCode,
      score: currentScore,
      timeoutEndTimestamp:
        timestamp +
        (state.scoreConfiguration.timeoutDurationSeconds || 30) * 1000,
      timeDelta: action.receivedTimestamp
        ? timestamp - action.receivedTimestamp
        : 0,
    },
  };
};

const cancelTimeout: UpdateGameStateFunction<"[Timeouts] Cancel timeout"> = (
  action,
  state,
) => {
  return {
    lastTimeout: state.lastTimeout && { ...state.lastTimeout, cancelled: true },
  };
};

const substitution: UpdateGameStateFunction<"[Lineups] Substitution"> = (
  action,
  state,
) => {
  const { teamCode, playerInId, playerOutId } = action.payload;
  const player = getPlayerFromTeam(playerInId, state.teamSquads[teamCode]);
  if (player) {
    let { currentSet, substitutions, currentScore } = state;

    currentSet = {
      ...currentSet,
      substitutionsRemaining: {
        ...currentSet.substitutionsRemaining,
        [teamCode]: currentSet.substitutionsRemaining[teamCode] - 1,
      },
      substitutionsUsed: {
        ...currentSet.substitutionsUsed,
        [teamCode]: currentSet.substitutionsUsed[teamCode] + 1,
      },
    };

    substitutions = {
      ...substitutions,
      [teamCode]: [
        ...substitutions[teamCode],
        {
          set: currentSet.setNumber,
          score: currentScore,
          playerInId,
          playerOutId,
          isExceptional: false,
        },
      ],
    };

    return {
      currentSet,
      currentLineups: {
        ...state.currentLineups,
        [teamCode]: exchangePlayer(
          state.currentLineups[teamCode],
          player,
          playerOutId,
        ),
      },
      substitutions,
    };
  } else {
    console.error(`No player found with id ${playerInId} in ${teamCode}`);
    return state;
  }
};

const exceptionalSubstitution: UpdateGameStateFunction<
  "[Lineups] Exceptional Substitution"
> = (action, state) => {
  const { teamCode, playerInId, playerOutId } = action.payload;
  const player = getPlayerFromTeam(playerInId, state.teamSquads[teamCode]);
  if (player) {
    let { currentSet, substitutions, currentScore } = state;

    currentSet = {
      ...currentSet,
      exceptionalSubstitutionsUsed: {
        ...currentSet.exceptionalSubstitutionsUsed,
        [teamCode]: currentSet.exceptionalSubstitutionsUsed[teamCode] + 1,
      },
    };

    substitutions = {
      ...substitutions,
      [teamCode]: [
        ...substitutions[teamCode],
        {
          set: currentSet.setNumber,
          score: currentScore,
          playerInId,
          playerOutId,
          isExceptional: true,
        },
      ],
    };

    return {
      currentSet,
      currentLineups: {
        ...state.currentLineups,
        [teamCode]: exchangePlayer(
          state.currentLineups[teamCode],
          player,
          playerOutId,
        ),
      },
      substitutions,
    };
  } else {
    console.error(`No player found with id ${playerInId} in ${teamCode}`);
    return state;
  }
};

const rotateClockwise: UpdateGameStateFunction<"[Lineups] Rotate clockwise"> = (
  action,
  state,
) => {
  const teamCode = action.payload;
  return {
    currentLineups: {
      ...state.currentLineups,
      [teamCode]: rotateLineupClockwise(
        state.currentLineups[teamCode],
        state.liberoExchangePlayer?.[teamCode],
      ),
    },
  };
};

const rotateAntiClockwise: UpdateGameStateFunction<
  "[Lineups] Rotate anticlockwise"
> = (action, state) => {
  const teamCode = action.payload;
  return {
    currentLineups: {
      ...state.currentLineups,
      [teamCode]: rotateLineupCounterClockwise(state.currentLineups[teamCode]),
    },
  };
};

const switchSides: UpdateGameStateFunction<"[TeamSides] Switch Sides"> = (
  action,
  state,
) => ({
  teamSides: {
    leftTeam: otherTeam(state.teamSides.leftTeam),
    rightTeam: otherTeam(state.teamSides.rightTeam),
  },
});

const start: UpdateGameStateFunction<"[TimeKeeping] Start"> = (
  action,
  state,
) => ({
  isSetPauseOngoing: false,
  currentSet: { ...state.currentSet, startTime: action.payload },
});

type SanctionMapConstraint = Partial<
  Record<UpdateActionType, SanctionEventTypeKeys>
>;

const actionTypeSanctionMap = {
  "[Sanction] Team Improper Request": "TEAM_IMPROPER_REQUEST",
  "[Sanction] Team Warning": "TEAM_WARNING",
  "[Sanction] Team Penalty": "TEAM_PENALTY",
  "[Sanction] Individual Warning": "INDIVIDUAL_WARNING",
  "[Sanction] Individual Penalty": "INDIVIDUAL_PENALTY",
  "[Sanction] Individual Expulsion": "INDIVIDUAL_EXPULSION",
  "[Sanction] Individual Disqualification": "INDIVIDUAL_DISQUALIFICATION",
} as const satisfies SanctionMapConstraint;
type SanctionActionKeys = keyof typeof actionTypeSanctionMap;

const addSanction: UpdateGameStateFunction<SanctionActionKeys> = (
  action,
  state,
) => {
  const { currentSetNumber, teamCode, setScore } = action.payload;
  const teamMemberId =
    "teamMemberId" in action.payload ? action.payload.teamMemberId : undefined;

  return {
    sanctions: {
      ...state.sanctions,
      [teamCode]: [
        ...state.sanctions[teamCode],
        createSanction({
          teamSquad: state.teamSquads[teamCode],
          type: actionTypeSanctionMap[action.type],
          set: currentSetNumber,
          score: setScore,
          teamMemberId,
        }),
      ],
    },
  };
};

const teamPenalty: UpdateGameStateFunction<"[Sanction] Team Penalty"> = (
  action,
  state,
) => {
  const { teamCode } = action.payload;

  return {
    ...score(
      { ...action, payload: otherTeam(teamCode), type: "[Set] Score" },
      state,
    ),
    ...addSanction(action, state),
  };
};

const individualPenalty: UpdateGameStateFunction<
  "[Sanction] Individual Penalty"
> = (action, state) => {
  const { currentSetNumber, teamCode, setScore, teamMemberId } = action.payload;

  return {
    ...score(
      { ...action, payload: otherTeam(teamCode), type: "[Set] Score" },
      state,
    ),
    ...addSanction(action, state),
  };
};

const createSanction = ({
  type,
  set,
  score,
  teamSquad,
  teamMemberId,
}: {
  teamSquad: DeepReadonly<TeamSquad>;
  type: SanctionEventTypeKeys;
  set: number;
  score: Score;
  teamMemberId?: string;
}): DeepReadonly<Sanction> => {
  const person = teamMemberId
    ? [...teamSquad.players, ...teamSquad.officials].find(
        (person) => person.uuid === teamMemberId,
      )
    : undefined;

  return {
    type,
    set,
    score,
    person,
  };
};

const challengeStarted: UpdateGameStateFunction<"[Challenges] Challenge"> = (
  action,
  state,
) => {
  const timeDelta = action.receivedTimestamp
    ? action.payload.timestamp - action.receivedTimestamp
    : 0;

  const challenge: StartedChallenge = {
    id: `${action.orderRank}`,
    challenger: action.payload.challenger,
    setScoreBeforeChallenge: action.payload.setScoreBeforeChallenge,
    setNumber: action.payload.setNumber,
    teamThatLastScored: action.payload.teamThatLastScored,
    challengeType: action.payload.challengeType,
    timestamp: action.payload.timestamp,
    challengesLeft: action.payload.challengesLeft,
  };

  return {
    isChallengeOngoing: true,
    lastChallenge: { ...challenge, timeDelta },
    challenges: [...state.challenges, challenge],
  };
};

const challengeDeclined: UpdateGameStateFunction<
  "[Challenges] Challenge Declined"
> = (action, state) => {
  const lastChallenge: LastChallenge = {
    ...state.lastChallenge!,
    ...action.payload,
    setScoreBeforeRally:
      action.payload.challengeMoment === "DURING_RALLY"
        ? action.payload.setScoreBeforeChallenge
        : getScoreFromHistory(state.scoreHistory, -1).score,
  };

  return {
    isChallengeOngoing: false,
    lastChallenge,
    challenges: state.challenges.map((challenge) =>
      isChallengeEqual(challenge, lastChallenge) ? lastChallenge : challenge,
    ),
  };
};

const challengeSuccess: UpdateGameStateFunction<
  "[Challenges] Challenge success"
> = (action, state) => {
  let updatedScore = {};

  if (action.payload.challenger === "referee") {
    if (
      !["POINT_TEAM1", "POINT_TEAM2", "DOUBLE_FAULT"].includes(
        action.payload.outcome,
      )
    ) {
      throw new Error(
        "Referee Challenge with success has no POINT_TEAM or DOUBLE_FAULT outcome",
      );
    }
    if (action.payload.outcome !== "DOUBLE_FAULT") {
      const teamReference: TeamReference =
        action.payload.outcome === "POINT_TEAM1" ? "team1" : "team2";
      updatedScore = score(
        { ...action, payload: teamReference, type: "[Set] Score" },
        state,
      );
    }
  } else if (["POINT_REVERSED", "CONFIRMED"].includes(action.payload.outcome)) {
    // POINT_REVERSED is taken care of by rebuilding the game state
    updatedScore = score(
      { ...action, payload: action.payload.challenger, type: "[Set] Score" },
      state,
    );
  }

  const lastChallenge: LastChallenge = {
    ...state.lastChallenge!,
    ...action.payload,
    ...("currentScore" in updatedScore
      ? { setScoreAfterChallenge: updatedScore.currentScore as Score }
      : {}),
    setScoreBeforeRally:
      action.payload.challengeMoment === "DURING_RALLY"
        ? action.payload.setScoreBeforeChallenge
        : getScoreFromHistory(state.scoreHistory, -1).score,
  };

  return {
    ...updatedScore,
    isChallengeOngoing: false,
    lastChallenge,
    challenges: state.challenges.map((challenge) =>
      isChallengeEqual(challenge, lastChallenge) ? lastChallenge : challenge,
    ),
  };
};

const challengeUnsuccessful: UpdateGameStateFunction<
  "[Challenges] Challenge unsuccessful"
> = (action, state) => {
  if (action.payload.challenger === "referee") {
    throw new Error("A referee can't have an unsuccessful challenge!");
  }

  const isEndOfSetChallenge = state.isSetPauseOngoing;
  const timeDelta = action.receivedTimestamp
    ? action.timestamp - action.receivedTimestamp
    : 0;

  const expectedNextSetStartTime =
    action.timestamp +
    state.scoreConfiguration.setPauseDurationSeconds * 1000 -
    timeDelta;

  const setWithSubtractedChallenges = (setStat: SetStat) => {
    if (action.payload.challenger === "referee") {
      return setStat;
    } else {
      return {
        ...setStat,
        challengesRemaining: {
          ...setStat.challengesRemaining,
          [action.payload.challenger]:
            setStat.challengesRemaining[action.payload.challenger] - 1,
        },
        challengesUsed: {
          ...setStat.challengesUsed,
          [action.payload.challenger]:
            setStat.challengesUsed[action.payload.challenger] + 1,
        },
      };
    }
  };

  const otherTeamScores = action.payload.challengeMoment === "DURING_RALLY";
  const updatedScore = otherTeamScores
    ? score(
        {
          ...action,
          payload: otherTeam(action.payload.challenger),
          type: "[Set] Score",
        },
        state,
      )
    : {};

  const setScoreBeforeRally =
    action.payload.challengeMoment === "DURING_RALLY"
      ? action.payload.setScoreBeforeChallenge
      : getScoreFromHistory(state.scoreHistory, -1).score;
  const setScoreAfterChallenge = otherTeamScores
    ? updatedScore.currentScore
    : state.lastChallenge?.setScoreBeforeChallenge;

  const lastChallenge: LastChallenge = {
    ...state.lastChallenge!,
    ...action.payload,
    setScoreAfterChallenge,
    setScoreBeforeRally,
  };

  return {
    ...updatedScore,
    isChallengeOngoing: false,
    expectedNextSetStartTime: isEndOfSetChallenge
      ? expectedNextSetStartTime
      : state.expectedNextSetStartTime,
    lastChallenge,
    challenges: state.challenges.map((challenge) =>
      isChallengeEqual(challenge, lastChallenge) ? lastChallenge : challenge,
    ),
    currentSet: !isEndOfSetChallenge
      ? setWithSubtractedChallenges(state.currentSet)
      : state.currentSet,
    playedSets: isEndOfSetChallenge
      ? {
          ...state.playedSets,
          [state.currentSet.setNumber - 1]: setWithSubtractedChallenges(
            state.playedSets[state.currentSet.setNumber - 1],
          ),
        }
      : state.playedSets,
  };
};

const challengeUnresolved: UpdateGameStateFunction<
  "[Challenges] Challenge Unresolved"
> = (action, state) => {
  const lastChallenge: LastChallenge = {
    ...state.lastChallenge!,
    ...action.payload,
    setScoreAfterChallenge: state.lastChallenge?.setScoreBeforeChallenge,
    setScoreBeforeRally: action.payload.setScoreBeforeChallenge,
  };

  return {
    isChallengeOngoing: false,
    lastChallenge,
    challenges: state.challenges.map((challenge) =>
      isChallengeEqual(challenge, lastChallenge) ? lastChallenge : challenge,
    ),
  };
};

const confirmFinalizationTeam: UpdateGameStateFunction<
  "[Match] Confirm finalization team"
> = (action, state) => ({
  teamConfirmations: {
    ...state.teamConfirmations,
    [action.payload.teamCode]: Confirmation.GAME,
  },
});

const confirmFinalizationReferee: UpdateGameStateFunction<
  "[Match] Confirm finalization referee"
> = (action, state) => ({
  teamConfirmations: {
    ...state.teamConfirmations,
    ref: Confirmation.GAME,
  },
});

const confirmFinalization: UpdateGameStateFunction<
  "[Match] Confirm finalization"
> = () => ({
  gameProgress: GameProgress.GAME_FINALIZED,
});

const confirmFinalizationSuccess: UpdateGameStateFunction<
  "[Match] Confirm finalization success"
> = () => ({
  gameProgress: GameProgress.FINALIZATION_SENT,
});

const confirmFinalizationFail: UpdateGameStateFunction<
  "[Match] Confirm finalization fail"
> = () => ({
  gameProgress: GameProgress.FINALIZATION_FAILED,
});

/**
 -----------------
 Helper functions
 -----------------
 **/

const markLicenseWithApprovalState = (
  approved: boolean,
  personType: "players" | "officials",
  action: GenericUpdateAction<
    | "[Match] Approved player license"
    | "[Match] Approved official license"
    | "[Match] Denied player license"
    | "[Match] Denied official license"
  >,
  teams: DeepReadonly<Teams>,
) => {
  const { uuid, teamCode } = action.payload;
  const team = teams[teamCode];
  const persons = team[personType].map((person) =>
    person.uuid === uuid
      ? { ...person, approvedOfficialLicense: approved }
      : person,
  );
  return {
    teams: {
      ...teams,
      [teamCode]: { ...teams[teamCode], [personType]: persons },
    },
  };
};

export const getPlayerFromTeam = (
  uuid: string,
  team: DeepReadonly<TeamSquad | InitializeMatchPayload["team1"]>,
): Player | undefined => {
  const player = team.players.find((player) => player.uuid === uuid);
  if (player) {
    return {
      uuid: player.uuid,
      firstName: player.firstName,
      lastName: player.lastName,
      jerseyNumber: player.jerseyNumber,
      position: player.position,
      isNationalityGerman:
        "isNationalityGerman" in player
          ? player.isNationalityGerman
          : !(player as ExtendedPlayer).license?.itc,
      portraitPhoto:
        "portraitPhoto" in player
          ? player.portraitPhoto && typeof player.portraitPhoto !== "string"
            ? player.portraitPhoto.url
            : player.portraitPhoto
          : "",
      isLibero:
        "liberos" in team &&
        team.liberos.map((l) => l.uuid).includes(player.uuid),
      isTeamCaptain: "captain" in team && team.captain?.uuid === player.uuid,
      ...("approvedOfficialLicense" in player
        ? { approvedOfficialLicense: player.approvedOfficialLicense }
        : {}),
      admissionStatus:
        "admissionStatus" in player ? player.admissionStatus : "ADMITTED",
      personUuid: player.personUuid,
    };
  } else {
    return undefined;
  }
};

const getOfficialFromTeam = (
  uuid: string,
  team: DeepReadonly<InitializeMatchPayload["team1"]>,
): Official | undefined => {
  const official = team.officials.find((player) => player.uuid === uuid);
  if (official) {
    return {
      uuid: official.uuid,
      firstName: official.firstName,
      lastName: official.lastName,
      portraitPhoto: official.portraitPhoto?.url,
      function: official.function,
      ...("approvedOfficialLicense" in official
        ? { approvedOfficialLicense: official.approvedOfficialLicense }
        : {}),
      admissionStatus: "ADMITTED",
      personUuid: official.personUuid,
    };
  } else {
    return undefined;
  }
};

const getTeamSquadWithToggled = <
  T extends keyof Omit<TeamSquad, "captain" | "confirmed">,
>(
  type: T,
  teamSquad: DeepReadonly<TeamSquad>,
  toggledSquadMember: DeepReadonly<TeamSquad[T][number]>,
): DeepReadonly<TeamSquad> => {
  const existingSquadMember = teamSquad[type].find(
    (squadMember) =>
      squadMember.personUuid === toggledSquadMember.personUuid ||
      ("function" in squadMember &&
        "function" in toggledSquadMember &&
        squadMember.function === toggledSquadMember.function),
  );
  if (existingSquadMember) {
    const newTypeList = teamSquad[type].filter(
      (squadMember) => squadMember.personUuid !== toggledSquadMember.personUuid,
    );

    if (
      "function" in existingSquadMember &&
      "function" in toggledSquadMember &&
      existingSquadMember.function !== toggledSquadMember.function
    ) {
      newTypeList.push(toggledSquadMember);
    }

    return {
      ...teamSquad,
      [type]: newTypeList,
    };
  } else {
    return {
      ...teamSquad,
      [type]: [...teamSquad[type], toggledSquadMember],
    };
  }
};

export const isDecidingSet = (
  currentSet: number,
  setsToWin: number,
  alwaysPlayMaximumNumberOfSets: boolean,
) => !alwaysPlayMaximumNumberOfSets && currentSet === setsToWin * 2 - 1;

const rotateLineupClockwise = (
  lineup: CurrentLineup,
  liberoSubstitutionPlayer?: Player,
): CurrentLineup => {
  const liberoRotatesToFront = lineup["4"]?.isLibero;
  return {
    0: lineup[1],
    1: lineup[2],
    2: lineup[3],
    3: liberoRotatesToFront ? liberoSubstitutionPlayer : lineup[4],
    4: lineup[5],
    5: lineup[0],
  };
};

const rotateLineupCounterClockwise = (lineup: CurrentLineup): CurrentLineup => {
  return {
    0: lineup[5],
    1: lineup[0],
    2: lineup[1],
    3: lineup[2],
    4: lineup[3],
    5: lineup[4],
  };
};

const createNewSet = (
  setNumber: number,
  timeoutsPerSet = 2,
  substitutionsPerSet = 6,
  challengesPerSet = 2,
  startTime?: number,
): SetStat => {
  return {
    setNumber,
    challengesUsed: emptyScore(),
    substitutionsUsed: emptyScore(),
    timeoutsUsed: emptyScore(),
    result: emptyScore(),
    timeoutsRemaining: {
      team1: timeoutsPerSet,
      team2: timeoutsPerSet,
    },
    substitutionsRemaining: {
      team1: substitutionsPerSet,
      team2: substitutionsPerSet,
    },
    challengesRemaining: {
      team1: challengesPerSet,
      team2: challengesPerSet,
    },
    exceptionalSubstitutionsUsed: emptyScore(),
    startTime,
  };
};

const exchangePlayer = (
  lineup: CurrentLineup,
  playerIn: Player,
  playerOutUuid: string,
): CurrentLineup => {
  return Object.entries(lineup)
    .map(([pos, player]): [string, Player] =>
      player?.uuid === playerOutUuid ? [pos, playerIn] : [pos, player],
    )
    .reduce((collect, [pos, player]) => ({ ...collect, [pos]: player }), {});
};

const emptyScore: (initialPoints?: number) => Score = (initialPoints = 0) => ({
  team1: initialPoints,
  team2: initialPoints,
});

const emptyTeamSquad: () => TeamSquad = () => ({
  players: [],
  officials: [],
  liberos: [],
  captain: undefined,
  confirmed: false,
});

export const defaultGameState: () => GameState = () => ({
  samsId: "",
  samsScoreVersion: "",
  lastAction: { orderRank: -1 } as UpdateActionWithState,
  lastVisibleAction: { orderRank: -1 } as UpdateActionWithState,
  actionHistory: {},
  currentLineups: { team1: {}, team2: {} },
  currentScore: emptyScore(),
  currentSetScore: emptyScore(),
  currentSet: createNewSet(0),
  playedSets: [],
  scoreHistory: { [1]: [[0, 0, 0]] },
  serving: "team1",
  initialServingTeam: "team1",
  teamSquads: { team1: emptyTeamSquad(), team2: emptyTeamSquad() },
  teams: {
    team1: {} as GameState["teams"]["team1"],
    team2: {} as GameState["teams"]["team2"],
  },
  gameProgress: GameProgress.CREATED,
  scoresheetPdfUrl: "",
  teamSides: { leftTeam: "team1", rightTeam: "team2" },
  scoreConfiguration: {} as InitializeMatchPayload["scoreConfiguration"],
  matchConfiguration: {} as InitializeMatchPayload["matchConfiguration"],
  arbitration: {} as InitializeMatchPayload["arbitration"],
  location: {} as InitializeMatchPayload["location"],
  matchNumber: 0,
  numberOfSpectators: 0,
  liberoExchangePlayer: { team1: undefined, team2: undefined },
  teamConfirmations: {
    team1: Confirmation.NONE,
    team2: Confirmation.NONE,
    referee: Confirmation.NONE,
  },
  isSetPauseOngoing: false,
  isChallengeOngoing: false,
  challenges: [],
  sanctions: { team1: [], team2: [] },
  substitutions: { team1: [], team2: [] },
  gender: "FEMALE",
  startTime: 0,
  timeFirstScoreInMatch: 0,
  comments: "",
  isChecksumValid: true,
  isSetterPlayerKnown: false,
  unknownUpdateActionTypes: new Map<UpdateActionType, object[]>(),
});

const UpdateFunctionMap: {
  [T in UpdateActionType]?: UpdateGameStateFunction<T>;
} = {
  "[Lineups] Cancel nomination": cancelNomination,
  "[Lineups] Libero in": liberoIn,
  "[Lineups] Libero out": liberoOut,
  "[Lineups] Libero switch": liberoSwitch,
  "[Lineups] Nominate": nominate,
  "[Lineups] Rotate anticlockwise": rotateAntiClockwise,
  "[Lineups] Rotate clockwise": rotateClockwise,
  "[Lineups] Substitution": substitution,
  "[Lineups] Exceptional Substitution": exceptionalSubstitution,
  "[MatchSet] Lock starting six": lockStartingSix,
  "[MatchState] Initialize Match": initializeMatch,
  "[Match] Add Official": addOfficial,
  "[Match] Add Player": addPlayer,
  "[Match] Approved official license": approvedOfficialLicense,
  "[Match] Denied official license": deniedOfficialLicense,
  "[Match] Approved player license": approvedPlayerLicense,
  "[Match] Denied player license": deniedPlayerLicense,
  "[Match] Change Jersey Number": changeJerseyNumber,
  "[Match] Change jersey": changeJersey,
  "[Match] Set shirt color": setShirtColor,
  "[Match] Set shorts color": setShortsColor,
  "[Match] Confirm arbitration": confirmArbitration,
  "[Match] Confirm finalization success": confirmFinalizationSuccess,
  "[Match] Confirm finalization fail": confirmFinalizationFail,
  "[Match] Confirm finalization team": confirmFinalizationTeam,
  "[Match] Confirm finalization referee": confirmFinalizationReferee,
  "[Match] Confirm finalization": confirmFinalization,
  "[Match] Save Scoresheet success": ignoreAction,
  "[Match] Save Scoresheet fail": ignoreAction,
  "[Match] Edit Arbitration Member": editArbitrationMember,
  "[Match] Edit Player": editPlayer,
  "[Match] Edit Official": editOfficial,
  "[Match] Edit number of Spectators": editNumberOfSpectators,
  "[Match] Edit start time": editStartTime,
  "[Match] Save Comments": saveComments,
  "[Sanction] Team Improper Request": addSanction,
  "[Sanction] Team Warning": addSanction,
  "[Sanction] Team Penalty": teamPenalty,
  "[Sanction] Individual Penalty": individualPenalty,
  "[Sanction] Individual Warning": addSanction,
  "[Sanction] Individual Expulsion": addSanction,
  "[Sanction] Individual Disqualification": addSanction,
  "[Serving] Change Serving": changeServing,
  "[Set] Score": score,
  "[Set] Score override": scoreOverride,
  "[TeamSides] Switch Sides": switchSides,
  "[TeamSquad] confirm": confirmTeamSquad,
  "[TeamSquad] load": loadTeamSquad,
  "[TeamSquad] reset all": resetTeamSquad,
  "[TeamSquad] select all": selectAll,
  "[TeamSquad] toggle captain": toggleCaptain,
  "[TeamSquad] toggle libero": toggleLibero,
  "[TeamSquad] toggle official": toggleOfficial,
  "[TeamSquad] toggle player": togglePlayer,
  "[TeamSquad] undo confirmation": undoTeamSquadConfirmation,
  "[TimeKeeping] Start Match": startMatch,
  "[TimeKeeping] Start": start,
  "[Timeouts] Change set pause duration": changeSetPauseDuration,
  "[Timeouts] Start timeout": startTimeout,
  "[Timeouts] Cancel timeout": cancelTimeout,
  "[Timeouts] Cancel set pause": cancelSetPause,
  "[Undoable] Undo last action": ignoreAction,
  "[Undoable] Redo previous action": ignoreAction,
  "[Match] Toggle Referee": ignoreAction,
  "[Match] Toggle libero registration": ignoreAction,
  "[Liberos] Set default libero": ignoreAction,
  "[Liberos] Libero unable to play": liberoUnableToPlay,
  "[Liberos] Add new libero": addNewLibero,
  "[TeamHighlight] Highlight": ignoreAction,
  "[Mvps] Select mvp": ignoreAction,
  "[Timeouts] Cancel technical timeout": ignoreAction,
  "[Challenges] Challenge": challengeStarted,
  "[Challenges] Challenge Declined": challengeDeclined,
  "[Challenges] Challenge success": challengeSuccess,
  "[Challenges] Challenge unsuccessful": challengeUnsuccessful,
  "[Challenges] Challenge Unresolved": challengeUnresolved,
};
